// src/pages/RoleManagement/modals/ApiPermissionAssignModal.tsx
// Popup "Phân quyền API" cho 1 role — hoàn toàn tách biệt với "Cập nhật permission"
// (PermissionAssignModal quyết định role thấy được UI/màn hình nào; popup này quyết định role được
// phép gọi API nào). Danh sách API permission được quét tự động từ controller các service
// (ApiPermissionDefinitionAttribute) và đồng bộ vào bảng Permission, không tạo tay như trước.

import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { fetchApiPermissions, fetchRoleApiPermissions, assignRoleApiPermissionsForRole } from 'slices/roleManagement/thunk';
import { RoleGroupDetail } from '../types';
import ApiPermissionTree from '../components/ApiPermissionTree';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface ApiPermissionAssignModalProps {
  isOpen: boolean;
  toggle: () => void;
  roleGroup: RoleGroupDetail | null;
  onSuccess?: () => void;
}

const ApiPermissionAssignModal: React.FC<ApiPermissionAssignModalProps> = ({
  isOpen,
  toggle,
  roleGroup,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { apiPermissions, assignedApiPermissionIds, loading } = useSelector(
    (state: any) => state.RoleManagement
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isSystemRole = roleGroup?.isProtected || roleGroup?.code === 'ADMIN';

  useEffect(() => {
    if (isOpen && roleGroup) {
      dispatch(fetchApiPermissions());
      dispatch(fetchRoleApiPermissions(roleGroup.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roleGroup?.id]);

  useEffect(() => {
    setSelectedIds(assignedApiPermissionIds || []);
  }, [assignedApiPermissionIds]);

  const handleClose = () => {
    toggle();
  };

  const handleSave = async () => {
    if (!roleGroup) return;

    setSubmitting(true);
    try {
      await dispatch(
        assignRoleApiPermissionsForRole({
          id: roleGroup.id,
          data: { permissionIds: selectedIds },
        })
      ).unwrap();

      toast.success(st('cap_nhat_phan_quyen_api_thanh_message'));
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      toast.error(error || st('co_loi_xay_ra_khi_cap_message'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!roleGroup) return null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {st('phan_quyen_api_title_2')} {roleGroup.name}
      </ModalHeader>
      <ModalBody>
        {isSystemRole && (
          <Alert color="warning" className="mb-3">
            <i className="ri-alert-line me-2"></i>
            {st('role_he_thong_protected_khong_the_message')}
          </Alert>
        )}
        <div className="text-muted small mb-3">
          {st('chon_nhung_api_ma_nhom_quyen_text')}
        </div>

        {loading && apiPermissions.length === 0 ? (
          <div className="placeholder-glow">
            <span className="placeholder col-12"></span>
          </div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <ApiPermissionTree
              items={apiPermissions}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              disabled={isSystemRole}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose}>
          {st('huy_button')}
        </Button>
        {PageActionPermission.canDisplay(location.pathname, "button-api-permission-assign-submit") && (
          <Button
            color="primary"
            onClick={handleSave}
            disabled={submitting || isSystemRole}
            data-action-code="button-api-permission-assign-submit"
            data-action-name="Lưu phân quyền API"
          >
            {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
            {st('luu_button')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ApiPermissionAssignModal;
